import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge } from '../../components/Badge';
import { PageHeader } from '../../components/PageHeader';
import { Button } from '../../components/Button';
import { EmptyState } from '../../components/EmptyState';
import { ErrorNotice } from '../../components/ErrorNotice';
import { SkeletonTable } from '../../components/Skeleton';
import { useSession } from '../auth/queries';
import { StatTile } from '../reporting/StatTile';
import { BackupDeviceDialog } from './BackupDeviceDialog';
import { formatBytes, STATUS_TONES } from './presentation';
import {
  useBackupDevices,
  useBackupSummary,
  useBackupVerification,
  useSyncBackup,
} from './queries';

/**
 * Die Sicherungsuebersicht -- F5-01, Anforderung 2.
 *
 * **Kein oberflaechlicher Zustand.** Jede Zeile nennt den Zustand **und** die
 * Fehlerzahl **und** die Tage seit der letzten erfolgreichen Sicherung.
 * "Completed" allein ist die Auskunft, die am 07.09.2026 ein Geraet als in
 * Ordnung meldete, das seit dem 24.02. nicht mehr gesichert hatte.
 *
 * **`Mit Fehlern abgeschlossen` steht als eigene Zahl ueber der Liste**, nicht
 * bei `Abgeschlossen`. Eine Sicherung mit Fehlern ist keine gelungene mit
 * Anmerkung.
 *
 * **Der Abgleich ist ein Knopf und kein Zeitgeber.** Wer hierherkommt, will
 * den Stand von jetzt; wer die Seite offen liegen laesst, will keine Abfrage
 * je Minute bei einem fremden Dienst. Wann zuletzt abgeglichen wurde, steht
 * daneben -- ohne diese Angabe waere jede Zahl auf der Seite ohne Alter.
 */
export function BackupPage() {
  const { t, i18n } = useTranslation();
  const session = useSession();

  const [onlyFailing, setOnlyFailing] = useState(false);
  const [openDevice, setOpenDevice] = useState<string | null>(null);
  const [showVerification, setShowVerification] = useState(false);

  const summary = useBackupSummary();
  const devices = useBackupDevices(onlyFailing ? { only_failing: true } : {});
  const sync = useSyncBackup();
  const verification = useBackupVerification(showVerification);

  const mayManage = (session.data?.permissions ?? []).includes('backup.manage');

  // Beide Felder tragen im Schema einen Vorgabewert und sind damit wahlfrei.
  // Zusammengezogen: Fuer den Leser ist ein uebersprungener Partner dasselbe
  // wie ein gescheiterter Zugang -- er sieht in beiden Faellen weniger, als
  // dastehen sollte.
  const syncProblems = [...(sync.data?.errors ?? []), ...(sync.data?.skipped_partners ?? [])];

  return (
    <>
      <PageHeader
        title={t('backup.title')}
        subtitle={
          summary.data?.last_sync_at
            ? t('backup.lastSync', {
                when: new Date(summary.data.last_sync_at).toLocaleString(i18n.language),
              })
            : t('backup.neverSynced')
        }
        action={
          <div className="row">
            <Button
              variant="secondary"
              disabled={sync.isPending}
              onClick={() => {
                sync.mutate(undefined);
              }}
            >
              {sync.isPending ? t('backup.syncing') : t('backup.sync')}
            </Button>
            <Button
              variant="secondary"
              onClick={() => {
                setShowVerification(true);
              }}
            >
              {t('backup.verify')}
            </Button>
          </div>
        }
      />

      {sync.isError ? <ErrorNotice error={sync.error} /> : null}

      {/* Was der Anbieter nicht herausgab, steht hier und verschwindet nicht.
          Ein Abruf, der auf Fehler 13501 mit einer leeren Liste antwortet,
          meldet "keine Fehler" und meint "keine Antwort". */}
      {syncProblems.length > 0 ? (
        <section className="card">
          <h2>{t('backup.syncIncomplete')}</h2>
          <ul>
            {syncProblems.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
        </section>
      ) : null}

      <div className="kpi-grid">
        <StatTile
          label={t('backupStatus.failed')}
          value={String(summary.data?.failed ?? 0)}
          tone={(summary.data?.failed ?? 0) > 0 ? 'danger' : undefined}
          hint={t('backup.failedHint')}
        />
        {/* Eigene Kachel -- siehe Modulkopf. */}
        <StatTile
          label={t('backupStatus.completed_with_errors')}
          value={String(summary.data?.completed_with_errors ?? 0)}
          hint={t('backup.withErrorsHint')}
        />
        <StatTile
          label={t('backup.neverSucceeded')}
          value={String(summary.data?.never_succeeded ?? 0)}
          hint={t('backup.neverSucceededHint')}
        />
        <StatTile label={t('backup.devices')} value={String(summary.data?.total ?? 0)} />
      </div>

      {showVerification ? (
        <section className="card">
          <div className="card__header">
            <h2>{t('backup.verificationTitle')}</h2>
            <Button
              variant="ghost"
              onClick={() => {
                setShowVerification(false);
              }}
            >
              {t('app.close')}
            </Button>
          </div>
          <p className="table__muted">{t('backup.verificationHint')}</p>

          {verification.isError ? <ErrorNotice error={verification.error} /> : null}
          {verification.data?.passed ? (
            <Badge tone="success">
              {t('backup.verificationPassed', { count: verification.data.devices_checked })}
            </Badge>
          ) : null}
          {verification.data && !verification.data.passed ? (
            <ul>
              {verification.data.findings.map((finding) => (
                <li key={`${finding.kind}-${finding.device_label}`}>
                  <strong>{finding.device_label}</strong>{' '}
                  {t(`backupFinding.${finding.kind}`, { defaultValue: finding.kind })} —{' '}
                  {finding.detail}
                </li>
              ))}
            </ul>
          ) : null}
        </section>
      ) : null}

      <section className="card">
        <label className="field__checkbox">
          <input
            type="checkbox"
            checked={onlyFailing}
            onChange={(event) => {
              setOnlyFailing(event.target.checked);
            }}
          />
          {t('backup.onlyFailing')}
        </label>
      </section>

      <section className="card card--flush">
        {devices.isPending ? <SkeletonTable columns={6} /> : null}
        {devices.isError ? <ErrorNotice error={devices.error} /> : null}

        {devices.data?.length === 0 ? (
          <EmptyState
            icon="backup"
            title={onlyFailing ? t('backup.noFailing') : t('backup.noDevices')}
            hint={onlyFailing ? t('backup.noFailingHint') : t('backup.noDevicesHint')}
          />
        ) : null}

        {devices.data && devices.data.length > 0 ? (
          <div className="table-wrapper table-wrapper--tall">
            <table className="table">
              <thead>
                <tr>
                  <th scope="col">{t('backup.device')}</th>
                  <th scope="col">{t('backup.customer')}</th>
                  <th scope="col">{t('backup.status')}</th>
                  <th scope="col">{t('backup.errorCount')}</th>
                  <th scope="col">{t('backup.lastSuccess')}</th>
                  <th scope="col">{t('backup.storage')}</th>
                </tr>
              </thead>
              <tbody>
                {devices.data.map((device) => (
                  <tr
                    key={device.id}
                    data-clickable
                    onClick={() => {
                      setOpenDevice(device.id);
                    }}
                  >
                    <td>
                      <span className="table__subject">
                        {device.computer_name || device.account_name}
                      </span>
                      {device.data_sources.length > 0 ? (
                        <div className="table__muted">{device.data_sources.join(', ')}</div>
                      ) : null}
                    </td>
                    <td className="table__muted">{device.partner_name}</td>
                    <td>
                      <Badge tone={STATUS_TONES[device.last_status]}>
                        {t(`backupStatus.${device.last_status}`)}
                      </Badge>
                    </td>
                    {/* Die Fehlerzahl steht immer da -- auch die Null. Eine
                        leere Zelle liest sich wie "nicht erhoben". */}
                    <td className="table__number">{device.error_count}</td>
                    <td className="table__muted">
                      {device.last_success_at
                        ? t('backup.daysAgo', {
                            count: device.days_since_success ?? 0,
                            date: new Date(device.last_success_at).toLocaleDateString(
                              i18n.language,
                            ),
                          })
                        : t('backup.never')}
                    </td>
                    <td className="table__number">
                      {formatBytes(device.used_storage_bytes, i18n.language) ?? t('app.none')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </section>

      {openDevice ? (
        <BackupDeviceDialog
          deviceId={openDevice}
          mayManage={mayManage}
          onClose={() => {
            setOpenDevice(null);
          }}
        />
      ) : null}
    </>
  );
}
